/**
 * Identity Service
 * Fetches on-chain identity information from People Chains
 * Primary: Polkadot People Chain
 * Fallback: Kusama People Chain
 */

import { getPolkadotPeopleApi, getKusamaPeopleApi, isPapiReady } from '@/lib/papi.client';
import { reencodeAddress } from '@/_utils/ss58';
import type { OnChainIdentity } from '@/types';

/**
 * Helper to decode identity data field value
 * Identity fields can be Raw (with Uint8Array or FixedSizeBinary) or None
 * PAPI returns types like: { type: 'Raw3', value: FixedSizeBinary } or { type: 'None' }
 */
// eslint-disable-next-line sonarjs/cognitive-complexity -- Complex due to handling many PAPI binary data type variants
function decodeIdentityField(field: unknown): string | undefined {
	if (!field || typeof field !== 'object') return undefined;

	const fieldObj = field as { type?: string; value?: unknown };

	// Handle None type
	if (fieldObj.type === 'None' || !fieldObj.type) return undefined;

	// Handle Raw# types (Raw0, Raw1, Raw3, Raw15, etc.) - value is a FixedSizeBinary or Uint8Array
	if (fieldObj.type.startsWith('Raw')) {
		const value = fieldObj.value;

		// Direct Uint8Array
		if (value instanceof Uint8Array) {
			return new TextDecoder().decode(value);
		}

		// Handle PAPI's FixedSizeBinary - it extends Binary which has asBytes() method
		if (value && typeof value === 'object') {
			if ('asBytes' in value && typeof (value as { asBytes: () => Uint8Array }).asBytes === 'function') {
				const bytes = (value as { asBytes: () => Uint8Array }).asBytes();
				return new TextDecoder().decode(bytes);
			}

			if ('asHex' in value && typeof (value as { asHex: () => string }).asHex === 'function') {
				const hex = (value as { asHex: () => string }).asHex();
				const bytes = new Uint8Array(
					hex
						.slice(2)
						.match(/.{1,2}/g)
						?.map((byte: string) => parseInt(byte, 16)) || []
				);
				return new TextDecoder().decode(bytes);
			}

			if (Symbol.iterator in value) {
				try {
					const bytes = new Uint8Array([...(value as Iterable<number>)]);
					if (bytes.length > 0) {
						return new TextDecoder().decode(bytes);
					}
				} catch {
					// Iteration failed, continue
				}
			}
		}

		// Array of numbers
		if (Array.isArray(value)) {
			return new TextDecoder().decode(new Uint8Array(value));
		}

		// Already a string
		if (typeof value === 'string') {
			return value;
		}
	}

	return undefined;
}

/**
 * Get highest verification level from judgements array
 */
function getVerificationLevel(judgements: Array<[number, unknown]>): 'Reasonable' | 'KnownGood' | null {
	let hasReasonable = false;

	for (const [, judgement] of judgements) {
		const j = judgement as { type?: string };
		if (j.type === 'KnownGood') return 'KnownGood';
		if (j.type === 'Reasonable') hasReasonable = true;
	}

	return hasReasonable ? 'Reasonable' : null;
}

/**
 * Parse raw identity response into our OnChainIdentity format
 * PAPI returns identity data directly as { judgements, deposit, info } or undefined/null
 */
function parseIdentityResponse(identityData: unknown, source: 'polkadot' | 'kusama'): OnChainIdentity | null {
	// PAPI returns undefined/null if no identity found
	if (!identityData) return null;

	// Type the identity data - PAPI returns it directly (not wrapped in Option)
	const identity = identityData as {
		info?: {
			display?: unknown;
			legal?: unknown;
			web?: unknown;
			twitter?: unknown;
			email?: unknown;
		};
		judgements?: Array<[number, unknown]>;
		deposit?: bigint;
	};

	// Check if identity has info object
	if (!identity.info) return null;

	const displayName = decodeIdentityField(identity.info.display);
	if (!displayName) return null; // Identity without display name is not useful

	const judgements = identity.judgements || [];
	const verificationLevel = getVerificationLevel(judgements);
	const isVerified = verificationLevel !== null;

	return {
		displayName,
		isVerified,
		judgement: verificationLevel,
		legal: decodeIdentityField(identity.info.legal),
		web: decodeIdentityField(identity.info.web),
		twitter: decodeIdentityField(identity.info.twitter),
		email: decodeIdentityField(identity.info.email),
		source
	};
}

/**
 * Fetch identity from Polkadot People Chain
 */
async function fetchPolkadotIdentity(address: string): Promise<OnChainIdentity | null> {
	try {
		const api = await getPolkadotPeopleApi();
		// Re-encode with Polkadot prefix (0) for consistent lookup
		const polkadotAddress = reencodeAddress(address, 0);
		const identityData = await api.query.Identity.IdentityOf.getValue(polkadotAddress);
		return parseIdentityResponse(identityData, 'polkadot');
	} catch (error) {
		console.error('[Identity] Error fetching Polkadot identity:', error);
		return null;
	}
}

/**
 * Fetch identity from Kusama People Chain
 */
async function fetchKusamaIdentity(address: string): Promise<OnChainIdentity | null> {
	try {
		const api = await getKusamaPeopleApi();
		// Re-encode with Kusama prefix (2) for consistent lookup
		const kusamaAddress = reencodeAddress(address, 2);
		const identityData = await api.query.Identity.IdentityOf.getValue(kusamaAddress);
		return parseIdentityResponse(identityData, 'kusama');
	} catch (error) {
		console.error('[Identity] Error fetching Kusama identity:', error);
		return null;
	}
}

/**
 * Fetch on-chain identity for an address
 * Checks Polkadot first, falls back to Kusama if not found
 *
 * @param address - SS58 encoded address
 * @returns Identity info or null if not found
 */
export async function fetchIdentity(address: string): Promise<OnChainIdentity | null> {
	// Check Polkadot People Chain first
	const polkadotIdentity = await fetchPolkadotIdentity(address);
	if (polkadotIdentity) {
		return polkadotIdentity;
	}

	// Fallback to Kusama People Chain
	const kusamaIdentity = await fetchKusamaIdentity(address);
	if (kusamaIdentity) {
		return kusamaIdentity;
	}

	return null;
}

/**
 * Check if identity service is ready
 */
export function isIdentityServiceReady(): boolean {
	return isPapiReady();
}
